import { SpotOrderRecord, SpotOrderStatus } from 'api/v1/spot'
import { TrendColorizer } from 'components/TrendColorizer'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCancelSpotPendingIds, useSpotSymbols } from 'store/spot/hooks'
import { OrderDirection, OrderType } from 'store/spot/types'
import { DateFormatter } from 'utils/datetime'
import { isZero, toFixed } from 'utils/number'
import { findSymbolById, findSymbolByName } from 'utils/symbols'
import { calcOrderFilledSize } from 'hooks/useSizeCalculations'

import {
  MobileItemTitle,
  MobileItemData,
  MobileItemFlex,
  MobileColumnItem,
  MobileCardWrap,
  TriggerDone,
} from '../styles'
import { StringFormatter } from 'utils/StringFormatter'
import { getStopOrderType } from 'utils/getStopOrderTriggerDirection'
import { OrderStatus } from 'components/OrderStatus'

const MobileRowItem = memo<{
  row: SpotOrderRecord
}>(({ row }) => {
  const { t } = useTranslation()
  const {
    id = 0,
    symbol: symbolName = '',
    direction = OrderDirection.LONG,
    createdAt = 0,
    type = OrderType.LIMIT,
    triggerOn,
    quantity = 0,
    triggerDirection,
    price = 0,
    status,
    fillPrice = 0,
  } = row ?? {}
  const spotSymbols = useSpotSymbols()
  const symbol = findSymbolByName(spotSymbols, symbolName)
  const {
    baseCurrency = '',
    quoteCurrency = '',
    baseScale = 0,
    quoteScale = 0,
  } = symbol ?? {}

  const getSymbolInfo = (symbolId: number) => {
    const symbol = findSymbolById(spotSymbols, symbolId)
    const {
      baseCurrency = '',
      quoteCurrency = '',
      baseScale = 0,
      quoteScale = 0,
    } = symbol ?? {}

    return {
      baseCurrency,
      quoteCurrency,
      baseScale,
      quoteScale,
    }
  }

  const getFilledSizeQuantity = (row: SpotOrderRecord) => {
    const { symbolId, quantity = 0, unfilledQuantity = 0 } = row
    const symbol = getSymbolInfo(symbolId)
    const { baseScale } = symbol
    const filledSize = calcOrderFilledSize(quantity, unfilledQuantity)
    const fixedQuantity = toFixed(quantity, baseScale)
    const fixedFilledSize = toFixed(filledSize, baseScale)
    return {
      fixedFilledSize,
      fixedQuantity,
    }
  }

  const datetime = DateFormatter.toISODateTime(createdAt)

  const isStopOrder = !isZero(triggerOn)
  const orderType = isStopOrder
    ? getStopOrderType(type, direction, triggerDirection)
    : StringFormatter.startCase(type)

  const { fixedQuantity, fixedFilledSize } = getFilledSizeQuantity(row)

  const fixedPrice = toFixed(price, quoteScale)
  const average =
    fillPrice > 0 ? toFixed(fillPrice, quoteScale) + ' ' + quoteCurrency : '-'

  const cancelSpotPendingIds = useCancelSpotPendingIds()

  return (
    <MobileCardWrap>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Date</MobileItemTitle>
          <MobileItemData>{datetime}</MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Pair</MobileItemTitle>
          <MobileItemData>
            {baseCurrency}/{quoteCurrency}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Type</MobileItemTitle>
          <MobileItemData>{orderType}</MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Side</MobileItemTitle>
          <MobileItemData>
            <TrendColorizer
              trend={direction === OrderDirection.LONG}
              value={
                direction === OrderDirection.LONG
                  ? t('user-order-table-content-buy')
                  : t('user-order-table-content-sell')
              }
            />
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Filled</MobileItemTitle>
          <MobileItemData>
            <span>{fixedFilledSize}</span>
            <span>
              /{fixedQuantity} {baseCurrency}
            </span>
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Average</MobileItemTitle>
          <MobileItemData>{average}</MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Price</MobileItemTitle>
          <MobileItemData>
            {fixedPrice} {quoteCurrency}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Trigger Conditions</MobileItemTitle>
          <MobileItemData>
            {isZero(triggerOn) ? (
              '-'
            ) : (
              <>
                Last Price{' '}
                {triggerDirection === OrderDirection.LONG ? '>=' : '<='}{' '}
                {triggerOn}
                {status === SpotOrderStatus.PENDING && <TriggerDone />}
              </>
            )}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Status</MobileItemTitle>
          <MobileItemData>
            <OrderStatus status={status} />
          </MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
    </MobileCardWrap>
  )
})

export default MobileRowItem
